/**
 * 기울임 입체감 — 손가락이나 마우스가 올라간 쪽으로 카드가 살짝 기운다(작업지시서 6절 「2D 에 깊이감」).
 * 움직임을 줄여 달라는 기기 설정이면 아무것도 하지 않는다.
 */
export function tilt(node: HTMLElement, max = 8) {
    if (typeof window === 'undefined' || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return {};

    const move = (e: PointerEvent) => {
        const r = node.getBoundingClientRect();
        const x = (e.clientX - r.left) / r.width - 0.5;
        const y = (e.clientY - r.top) / r.height - 0.5;
        node.style.transform = `perspective(700px) rotateX(${(-y * max).toFixed(2)}deg) rotateY(${(x * max).toFixed(2)}deg)`;
    };
    const reset = () => {
        node.style.transform = '';
    };

    node.style.transition = 'transform 0.15s ease-out';
    node.addEventListener('pointermove', move);
    node.addEventListener('pointerleave', reset);
    node.addEventListener('pointerup', reset);

    return {
        destroy() {
            node.removeEventListener('pointermove', move);
            node.removeEventListener('pointerleave', reset);
            node.removeEventListener('pointerup', reset);
        },
    };
}

/**
 * 폰 기울기 — 폰을 기울이면 판(보관함 · 이번 턴 칸 · 솥)이 그쪽으로 살짝 기운다(최대 6도 · Windows 방울이 prod 지적).
 * 붙인 요소에 --gx · --gy(도)를 적고, CSS 가 그 값으로 기울인다. 처음 쥔 각도를 「평평함」 으로 삼고 천천히 따라간다
 * (누워서 보든 앉아서 보든 그 자세가 기준이 되게).
 * 안드로이드는 허락 없이 된다. 아이폰은 askGyroPermission() 을 누른 그 순간에 불러야 한다(🔇 단추로 소리를 켤 때).
 */
export function gyroTilt(node: HTMLElement, max = 6) {
    if (typeof window === 'undefined' || !('DeviceOrientationEvent' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return {};
    }

    let base: { beta: number; gamma: number } | null = null;
    let frame = 0;
    const clamp = (v: number) => Math.max(-max, Math.min(max, v));

    const onTurn = (e: DeviceOrientationEvent) => {
        if (e.beta === null || e.gamma === null) return;
        base ??= { beta: e.beta, gamma: e.gamma };
        base = { beta: base.beta + (e.beta - base.beta) * 0.02, gamma: base.gamma + (e.gamma - base.gamma) * 0.02 };
        const gx = clamp((e.gamma - base.gamma) / 3);
        const gy = clamp(-(e.beta - base.beta) / 3);
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
            node.style.setProperty('--gx', gx.toFixed(2));
            node.style.setProperty('--gy', gy.toFixed(2));
        });
    };

    window.addEventListener('deviceorientation', onTurn);

    return {
        destroy() {
            window.removeEventListener('deviceorientation', onTurn);
            cancelAnimationFrame(frame);
        },
    };
}

/** 아이폰 사파리의 「동작 및 방향 접근」 허락 — 누른 그 순간(사용자 동작 안)에 불러야 한다. 다른 기기에서는 아무것도 안 한다. */
export function askGyroPermission(): void {
    const D = (window as unknown as { DeviceOrientationEvent?: { requestPermission?: () => Promise<string> } }).DeviceOrientationEvent;
    if (typeof D?.requestPermission === 'function') {
        D.requestPermission().catch(() => {
            // 거절 — 기울임만 없을 뿐이다
        });
    }
}
